import { useCallback, type MutableRefObject } from 'react'
import { useNavigate, type Location } from 'react-router-dom'
import { errorCodes, getUserFriendlyErrorMessage } from '../../../utils/errormessages'
import { hasSettingsChanged, stripHtml } from '../helper'
import { useSaveStateSettings } from '../../management/StatesSettings/hooks/useSaveStateSettings'
import { LOGOUT } from '../../../app/authState'
import { sendCustomSentryMessage } from '../../../utils/sentryUtility'
import { useAppDispatch } from '../../../redux/hooks/useAppDispatch'
import type { ApplicationSettingsPerStateAdminApiContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'

const getLocationSearch = (state: unknown, fallback: string): string => {
  if (state !== null && typeof state === 'object' && 'search' in state && typeof state.search === 'string') {
    return state.search
  }
  return fallback
}

interface UseEditStateSettingsCallbacksParams {
  stateSettings: ApplicationSettingsPerStateAdminApiContract
  setError: (value: string) => void
  setLoading: (value: boolean) => void
  isPhoneVisitAllowed: boolean | undefined
  drugScreeningText: string | null | undefined
  requiresDrugScreening: boolean | null | undefined
  setDrugScreeningText: (value: string | null) => void
  setRequiresDrugScreening: (value: boolean) => void
  setIsPhoneVisitAllowed: (value: boolean) => void
  setIsConfirmationPopUpOpened: (value: boolean | 'back') => void
  setDrugError: (value: string) => void
  isConfirmationPopUpOpened: boolean | 'back'
  setNewActiveTab: (value: string | { redirect: string }) => void
  newActiveTab: string | { redirect: string }
  location: Location
  // Holds the destination requested via a nav-bar click while there were unsaved changes; navigated
  // to on discard, cleared on keep-editing. Null for Cancel/back, which fall back to their own logic.
  pendingNavigationRef: MutableRefObject<string | null>
}

export const useEditStateSettingsCallbacks = ({
  stateSettings,
  setError,
  setLoading,
  isPhoneVisitAllowed,
  drugScreeningText,
  requiresDrugScreening,
  setDrugScreeningText,
  setRequiresDrugScreening,
  setIsPhoneVisitAllowed,
  setIsConfirmationPopUpOpened,
  setDrugError,
  isConfirmationPopUpOpened,
  setNewActiveTab,
  newActiveTab,
  location,
  pendingNavigationRef,
}: UseEditStateSettingsCallbacksParams) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { saveStateSettings } = useSaveStateSettings()

  const setPageLoading = useCallback(
    (value: boolean) => {
      setLoading(value)
    },
    [setLoading],
  )

  const setToastErrorMessage = useCallback(
    (value: string) => {
      setError(value)
    },
    [setError],
  )

  const cancel = useCallback(() => {
    void navigate(`/management${getLocationSearch(location.state, '?activeTab=States')}`)
  }, [navigate, location.state])

  const save = useCallback(() => {
    const currentSettings: ApplicationSettingsPerStateAdminApiContract = {
      IsPhoneVisitCommunicationTypeAllowed: isPhoneVisitAllowed ?? false,
      DrugScreenHtml: drugScreeningText ?? null,
    }
    setIsConfirmationPopUpOpened(false)

    // HTML-aware empty check: an "empty" rich-text editor still emits markup (e.g. '<p><br></p>'),
    // so strip tags before deciding the required field is blank.
    if (requiresDrugScreening && stripHtml(drugScreeningText) === '') {
      setDrugError(getUserFriendlyErrorMessage(errorCodes.FIELD_REQUIRED, null, 'Drug screen rules'))
      setToastErrorMessage(getUserFriendlyErrorMessage(errorCodes.FILL_ALL_FIELDS))
      return
    }
    window.releaseListener = true
    if (hasSettingsChanged(currentSettings, stateSettings)) {
      saveStateSettings(stateSettings.StateCode ?? '', currentSettings, {
        onSetLoading: setPageLoading,
        onSuccess: () => {
          // Reproduces the old StateSettingsListener SAVE_STATE_SETTINGS_SUCCESS navigation.
          if (newActiveTab) {
            if (typeof newActiveTab === 'object' && newActiveTab.redirect === 'logout') {
              dispatch({ type: LOGOUT })
              sendCustomSentryMessage('Logout sidebar clicked state settings listener')
            } else if (typeof newActiveTab === 'object') {
              void navigate(`/${newActiveTab.redirect}`)
            }
          } else {
            void navigate(`/management${getLocationSearch(location.state, '?activeTab=States')}`)
          }
        },
        onError: setToastErrorMessage,
      })
    } else {
      cancel()
    }
  }, [
    isPhoneVisitAllowed,
    drugScreeningText,
    requiresDrugScreening,
    newActiveTab,
    stateSettings,
    setIsConfirmationPopUpOpened,
    setDrugError,
    setToastErrorMessage,
    saveStateSettings,
    setPageLoading,
    dispatch,
    navigate,
    location.state,
    cancel,
  ])

  const changeRequiresDrugScreening = useCallback(() => {
    if (requiresDrugScreening) {
      setDrugError('')
    }
    setRequiresDrugScreening(!requiresDrugScreening)
  }, [requiresDrugScreening, setDrugError, setRequiresDrugScreening])

  const changePhoneRule = useCallback(() => {
    setIsPhoneVisitAllowed(!isPhoneVisitAllowed)
  }, [isPhoneVisitAllowed, setIsPhoneVisitAllowed])

  const setDrugScreenRules = useCallback(
    (value: string) => {
      // Normalise an empty editor ('<p><br></p>') to '' so change-detection and the required-field
      // check treat it as blank; otherwise store the HTML verbatim and clear the error.
      const normalized = stripHtml(value) === '' ? '' : value
      setDrugScreeningText(normalized)
      if (normalized !== '') {
        setDrugError('')
      }
    },
    [setDrugScreeningText, setDrugError],
  )

  const confirmationPopUpDiscardBtnClick = useCallback(() => {
    window.releaseListener = true
    setIsConfirmationPopUpOpened(false)
    // A nav-bar click requested a specific destination — honour it and skip the back/cancel logic.
    const pendingNavigation = pendingNavigationRef.current
    if (pendingNavigation) {
      pendingNavigationRef.current = null
      setNewActiveTab('')
      void navigate(pendingNavigation)
      return
    }
    if (newActiveTab) {
      if (typeof newActiveTab === 'object' && newActiveTab.redirect === 'logout') {
        dispatch({ type: LOGOUT })
        sendCustomSentryMessage('Logout sidebar clicked, edit state setting discard pop up')
      } else if (typeof newActiveTab === 'object') {
        void navigate(`/${newActiveTab.redirect}`)
      }
    } else if (isConfirmationPopUpOpened === 'back') {
      window.forceBack = true
      window.history.back()
    } else {
      cancel()
    }
    setNewActiveTab('')
  }, [
    newActiveTab,
    isConfirmationPopUpOpened,
    setIsConfirmationPopUpOpened,
    dispatch,
    navigate,
    cancel,
    setNewActiveTab,
    pendingNavigationRef,
  ])

  const confirmationPopUpCloseBtnClick = useCallback(() => {
    pendingNavigationRef.current = null
    setIsConfirmationPopUpOpened(false)
    setNewActiveTab('')
  }, [setIsConfirmationPopUpOpened, setNewActiveTab, pendingNavigationRef])

  return {
    setPageLoading,
    setToastErrorMessage,
    save,
    cancel,
    changeRequiresDrugScreening,
    changePhoneRule,
    setDrugScreenRules,
    confirmationPopUpDiscardBtnClick,
    confirmationPopUpCloseBtnClick,
  }
}
